import { z } from 'zod';

export const CLASSIFIER_KEY_PATTERN = /^[a-zA-Z0-9-]+$/;

export const classifierTypes = ['enum', 'float', 'bool'] as const;
export type ClassifierType = (typeof classifierTypes)[number];

export const choiceOptionSchema = z.object({
  optionKey: z.string(),
  description: z.string(),
});

export type ChoiceOption = z.infer<typeof choiceOptionSchema>;

export const boolCriteriaSchema = z.object({
  true: z.string().optional(),
  false: z.string().optional(),
});

export type BoolCriteria = z.infer<typeof boolCriteriaSchema>;

export const classifierSchema = z.object({
  key: z.string(),
  type: z.enum(classifierTypes),
  instructions: z.string(),
  criteria: z.union([z.array(z.string()), z.array(choiceOptionSchema), boolCriteriaSchema]).optional(),
});

export type Classifier = z.infer<typeof classifierSchema>;

export function emptyScoreClassifier(): Classifier {
  return {
    key: '',
    type: 'float',
    instructions: '',
    criteria: ['', ''],
  };
}

export function criteriaForType(type: ClassifierType): Classifier['criteria'] {
  if (type === 'float') {
    return ['', ''];
  }
  if (type === 'enum') {
    return [{ optionKey: '', description: '' }];
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeCriteria(type: ClassifierType, criteria: unknown): Classifier['criteria'] {
  if (type === 'float') {
    if (!Array.isArray(criteria)) {
      return ['', ''];
    }
    return criteria.map((level) => (typeof level === 'string' ? level : ''));
  }

  if (type === 'enum') {
    if (!isRecord(criteria)) {
      return [{ optionKey: '', description: '' }];
    }
    const options = Object.entries(criteria).map(([optionKey, description]) => ({
      optionKey,
      description: typeof description === 'string' ? description : '',
    }));
    return options.length > 0 ? options : [{ optionKey: '', description: '' }];
  }

  if (!isRecord(criteria)) {
    return undefined;
  }
  return {
    true: typeof criteria.true === 'string' ? criteria.true : undefined,
    false: typeof criteria.false === 'string' ? criteria.false : undefined,
  };
}

export function normalizeClassifiersFromApi(value: unknown): Classifier[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!isRecord(item)) {
      return [];
    }
    const type = classifierTypes.find((candidate) => candidate === item.type) ?? 'float';
    return [
      {
        key: typeof item.key === 'string' ? item.key : '',
        type,
        instructions: typeof item.instructions === 'string' ? item.instructions : '',
        criteria: normalizeCriteria(type, item.criteria),
      },
    ];
  });
}

export type ClassifierPayload = {
  key: string;
  type: ClassifierType;
  instructions: string;
  criteria?: Record<string, string | null> | string[] | { true?: string; false?: string };
};

export function classifiersForApi(classifiers: Classifier[]): ClassifierPayload[] {
  return classifiers.map((classifier) => {
    const payload: ClassifierPayload = {
      key: classifier.key.trim(),
      type: classifier.type,
      instructions: classifier.instructions.trim(),
    };
    const criteria = criteriaForApi(classifier);
    if (criteria !== undefined) {
      payload.criteria = criteria;
    }
    return payload;
  });
}

function criteriaForApi(classifier: Classifier): ClassifierPayload['criteria'] {
  if (classifier.type === 'float' && Array.isArray(classifier.criteria)) {
    return classifier.criteria
      .filter((level): level is string => typeof level === 'string')
      .map((level) => level.trim());
  }

  if (classifier.type === 'enum' && Array.isArray(classifier.criteria)) {
    const options: Record<string, string | null> = {};
    for (const option of classifier.criteria) {
      if (typeof option === 'string' || !option.optionKey.trim()) {
        continue;
      }
      const description = option.description.trim();
      options[option.optionKey.trim()] = description === '' ? null : description;
    }
    return options;
  }

  if (classifier.type === 'bool' && classifier.criteria && !Array.isArray(classifier.criteria)) {
    const descriptions: { true?: string; false?: string } = {};
    if ('true' in classifier.criteria && classifier.criteria.true?.trim()) {
      descriptions.true = classifier.criteria.true.trim();
    }
    if ('false' in classifier.criteria && classifier.criteria.false?.trim()) {
      descriptions.false = classifier.criteria.false.trim();
    }
    if (descriptions.true === undefined && descriptions.false === undefined) {
      return undefined;
    }
    return descriptions;
  }

  return undefined;
}
